import {inject, Injectable} from '@angular/core';
import { WindowRefService } from '@services/window-ref-service';
import { EventLoggerService } from '@services/event-logger-service';
import { AppConfig } from 'client/app/app.config';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { ExperimentService } from '@services/experiment';
import { AovDetails, CartPricingResult, CartUpdateEventParams, CartUpdateOptions, CartUpdateResult } from '@shared/types/cart.types';
import { ApiClientConstant } from '@cureskin/api-client';
import { ActivePregnancyTag, UserService } from '@services/user/user.service';
import { ValueOf } from '@cureskin/api-client/src/common';
import { Catalog } from '@cureskin/api-client/src/server-api';
import { User } from 'parse';


import {ActivatedRoute, Router} from '@angular/router';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {Store} from '@ngrx/store';
import {ImageService} from '@services/image-service';
import {VariantsPopupComponent} from 'client/app/user/user-view/user-home/variants-popup/variants-popup.component';
import {toPlainObject, findSmallestMargUnitVariant, buildVariantDataList, resolveProductImage, resolveProductUnit} from 'client/helper/variants-popup-helper';
import * as fromActions from '@store/actions/app.actions';

@Injectable({providedIn: 'root'})
export class CartService {
  private readonly userService: UserService = inject(UserService);
  private readonly appConfig: AppConfig = inject(AppConfig);
  private readonly window: WindowRefService = inject(WindowRefService);
  private readonly eventLoggerService: EventLoggerService = inject(EventLoggerService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly experimentService: ExperimentService = inject(ExperimentService);

  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private readonly router: Router = inject(Router);
  private readonly imageService: ImageService = inject(ImageService);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly store: Store = inject(Store);

  private cart: any;
  private cartItems: any[] = [];
  private originalCashBalance: number = 0;

  // Skip variants check config: 'from' param for reorder path, 'section' for other paths
  private readonly skipVariantsCheckForFromParams: string[] = ['bogoProduct'];
  private readonly skipVariantsCheckForSections: string[] = ['saleProducts'];

  barStyle: {
    amountRemaining: number;
    width: string;
    backgroundColor: string;
  } = {
    amountRemaining: 0,
    width: '0%',
    backgroundColor: '#D66736',
  };

  constructor() {}

  async addOrRemoveFromCart(
    product: any,
    action: string,
    options: CartUpdateOptions
  ): Promise<CartUpdateResult> {
    const limit = product.prescriptionRequired
      ? this.appConfig.Shared.Product.CartLimits.RX_LIMIT
      : this.appConfig.Shared.Product.CartLimits.NON_RX_LIMIT;

    // Use passed cartItems if available, otherwise fetch from server
    let currentCartItems: any[] = [];
    if (options.cartItems && options.cartItems.length > 0) {
      currentCartItems = options.cartItems;
    } else {
      // Fallback: fetch cart only if cartItems not provided (e.g., wallet-view, product-controller)
      const currentCart = await this.conn.fetchNewCart(this.conn.user.id);
      currentCartItems = currentCart?.data?.lineItems || [];
    }
    const currentCartItem = currentCartItems.find((item: any): any => item.productId === product.objectId);
    let quantity = currentCartItem?.quantity ?? 0;

    // For REMOVE action, if product not found in passed cartItems, fetch from server to ensure accuracy
    if (action === this.appConfig.Shared.Cart.Action.REMOVE && quantity === 0 && options.cartItems && options.cartItems.length > 0) {
      // Product might have been added/removed elsewhere, fetch fresh cart data
      const currentCart = await this.conn.fetchNewCart(this.conn.user.id);
      currentCartItems = currentCart?.data?.lineItems || [];
      const freshCartItem = currentCartItems.find((item: any): any => item.productId === product.objectId);
      quantity = freshCartItem?.quantity ?? 0;
    }
    const eventParams = {
      objectId: product?.objectId,
      quantity: quantity,
      action: action,
      tag: options?.tag || 'no-tag',
      eventName: options?.eventName
    };
    if (action === this.appConfig.Shared.Cart.Action.ADD) {
      // Skip guard check if already done by caller (e.g., in component)
      let isOverriddenByDiscontinuedFlow = false;
      if (!options.skipGuardCheck) {
        const guardResult = await this.checkProductAllowedForCartAndPrompt(product, options);
        // Defensive: treat any non-true value (undefined/null/false) as a cancel and do not add
        if (!guardResult || guardResult.allowed !== true) {
          return this.createFailureResult(quantity, options.productsInCart);
        }
        if (guardResult.overrideProduct) {
          product = guardResult.overrideProduct;
          isOverriddenByDiscontinuedFlow = true;
          options.skipVariantsCheck = true;
        }
      }
      // Skip variants check when adding via bottomsheet (e.g. variants-popup), specific pages,
      // or when product was overridden by the discontinued popup flow
      if (!isOverriddenByDiscontinuedFlow && !this.shouldSkipVariantsCheck(options)) {
        const variantsResult = await this.checkVariantsAndShowPopupIfNeeded(product, options);
        if (variantsResult.shouldShowPopup) {
          return this.createFailureResult(quantity, options.productsInCart);
        }
      }
      // Check limit using current quantity (from passed cartItems or server)
      if (quantity >= limit) {
        this.broadcast.broadcast('NOTIFY', {
          message: `Maximum quantity is limited to ${limit} per product`
        });
        return this.createFailureResult(quantity, options.productsInCart);
      }
      this.sendCartUpdateEvent(eventParams);
      return this.handleAddProduct(product, quantity, options);
    }

    if (action === this.appConfig.Shared.Cart.Action.REMOVE && quantity > 0) {
      this.sendCartUpdateEvent(eventParams);
      return this.handleRemoveProduct(product, quantity, options);
    }

    return this.createFailureResult(quantity, options.productsInCart);
  }

  private async checkVariantsAndShowPopupIfNeeded(
    product: any,
    options: CartUpdateOptions,
  ): Promise<{ shouldShowPopup: boolean }> {
    try {
      if (product?.isSaleProduct) return { shouldShowPopup: false };

      const mainProductId =
        product?.mainProduct?.objectId ?? product?.mainProduct?.id ?? product?.objectId;
      if (!mainProductId) return { shouldShowPopup: false };

      const { variants } = await this.conn.getVariantsByMainProductId(mainProductId);
      if (!variants || variants.length <= 1) return { shouldShowPopup: false };

      await this.openVariantsPopup(product, variants, options.cartItems, options.section);
      return { shouldShowPopup: true };
    } catch {
      return { shouldShowPopup: false };
    }
  }

  async openVariantsPopup(
    product: any,
    variants: any[],
    cartItems?: any[],
    section?: string,
  ): Promise<void> {
    const cartItemsList = this.cartItems.length ? this.cartItems : (cartItems || []);
    const productJson = toPlainObject(product);
    const smallestMargUnitVariant = findSmallestMargUnitVariant(variants);
    const variantData = buildVariantDataList(variants, cartItemsList, productJson.objectId, section);
    const productImage = resolveProductImage(productJson, smallestMargUnitVariant, (url) => this.imageService.getURL(url));
    const unit = resolveProductUnit(productJson, smallestMargUnitVariant);

    const bannerData = {
      title: productJson.title ?? smallestMargUnitVariant?.title ?? '',
      productImage,
      variantData,
      unit,
    };

    const mainProductId = product?.mainProduct?.objectId ?? product?.mainProduct?.id ?? product?.objectId;

    this.bottomSheet.open(VariantsPopupComponent, {
      data: {
        panelClass: 'variants-popup-panel',
        bannerData,
        mainProductId,
        section,
        experimentVariantData: { variant: { uiVariants: { showProductPopup: true } } },
        callbacks: {
          closeBottomSheet: () => this.bottomSheet.dismiss(),
          addOrRemoveProductToCart: (productId: string, action: 'ADD' | 'REMOVE') =>
            this.addOrRemoveProductFromVariantsPopup(productId, action),
          handleProceedToBuy: (productTitle: string) => this.handleVariantsPopupProceedToBuy(productTitle, section),
        },
      },
    });
  }

  private async addOrRemoveProductFromVariantsPopup(productId: string, action: 'ADD' | 'REMOVE'): Promise<void> {
    try {
      await this.executeCartAction(productId, action);
      await this.syncCartWithStore();
      this.broadcast.broadcast('CART_UPDATED', { productId, action: action.toLowerCase() });
    } catch (error: any) {
      const errorMessage = error?.message ?? `Failed to ${action.toLowerCase()} product to cart`;
      this.broadcast.broadcast('NOTIFY', { message: errorMessage });
      throw error;
    }
  }

  private async executeCartAction(productId: string, action: 'ADD' | 'REMOVE'): Promise<void> {
    if (action === 'ADD') {
      await this.conn.addProductToNewCart({ productId, quantity: 1, section: 'variantsPopup' });
    } else {
      await this.conn.removeProductFromNewCart({ productId, quantity: 1 });
    }
  }

  private async syncCartWithStore(): Promise<void> {
    const updatedCart = await this.conn.fetchNewCart(this.conn.user.id);
    this.cartItems = updatedCart?.data?.lineItems || [];
    const cartProductQuantities: { [key: string]: number } = {};
    this.cartItems.forEach((item: any) => {
      cartProductQuantities[item.productId] = item.quantity;
    });
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: cartProductQuantities }));
  }

  private handleVariantsPopupProceedToBuy(productTitle: string, section?: string): void {
    this.bottomSheet.dismiss();
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({
      cta: 'variants_popup_proceed_to_buy',
      product_title: productTitle,
      section: section ?? 'unknown',
    }));
    this.router.navigate(['cart']);
  }


  async checkProductAllowedForCartAndPrompt(product: any, options?: CartUpdateOptions): Promise<{ allowed: boolean; overrideProduct?: any }> {
    try {
      const user: User = this.conn.getActingUser();
      const age = user?.get('Age') ? Number.parseInt(user?.get('Age'), 10) : null;
      const marketingTags = user.get('marketingTags') || [];
      const overridePregnancyCheck = options?.overridePregnancyCheck ?? false;
      const section = options?.section ?? '';

      const shouldShowTeenagerBottomSheet = this.shouldShowTeenagerBottomSheet({
        age,
        safteyProfile: product?.safteyProfile,
        section
      });

      if (shouldShowTeenagerBottomSheet) {
        this.broadcast.broadcast('OPEN_TEENAGER_RESTRICTION_BOTTOMSHEET', { product });
        return { allowed: false };
      }

      const pregnancyResult = this.shouldShowPregnancyBottomSheet({
        marketingTags,
        gender: user?.get('Gender'),
        safteyProfile: product?.safteyProfile,
        overridePregnancyCheck,
        section
      });

      if (pregnancyResult.show) {
        const { navigateToCartOnAdd, navigateToArticlePage, section } = options || {};
        this.broadcast.broadcast('OPEN_PREGNANCY_RESTRICTION_BOTTOMSHEET', {
          product,
          activeTag: pregnancyResult.activeTag,
          ...(navigateToCartOnAdd && { navigateToCartOnAdd }),
          ...(navigateToArticlePage && { navigateToArticlePage }),
          ...(section && { section }),
        });
        return { allowed: false };
      }

      const overrideApprovalCheck = options?.overrideApprovalCheck ?? false;
      const shouldShowApproval = this.shouldShowApprovalBottomSheet({
        requiresApproval: product?.requiresApproval,
        overrideApprovalCheck,
        section,
      });

      if (shouldShowApproval) {
        const { navigateToCartOnAdd, navigateToArticlePage, section } = options || {};
        this.broadcast.broadcast('OPEN_APPROVAL_BOTTOMSHEET', {
          product,
          ...(navigateToCartOnAdd && { navigateToCartOnAdd }),
          ...(navigateToArticlePage && { navigateToArticlePage }),
          ...(section && { section }),
        });
        return { allowed: false };
      }

      if (options?.skipDiscontinuedCheck) return { allowed: true };

      const status = product?.inventoryStatus;
      const { DISCONTINUED, RESTRICTED } = this.appConfig.Shared.Inventory.Type;
      const isBlocked = [DISCONTINUED, RESTRICTED].includes(status);
      if (!isBlocked) return { allowed: true };

      const catalog = await this.conn.findCatalogWithAlternateProduct(product.objectId);
      const alternate = catalog?.get ? catalog.get('alternateProduct') : catalog?.alternateProduct;

      // If no alternate product exists, show info popup and block
      if (!alternate) {
        this.broadcast.broadcast('OPEN_DISCONTINUED_INFO', { product });
        return { allowed: false };
      }
      const alternateJson = JSON.parse(JSON.stringify(alternate));
      const altStatus = alternateJson?.inventoryStatus; // parsing parse object to json
      const altBlocked = [DISCONTINUED, RESTRICTED].includes(altStatus);
      const altPrice = alternateJson?.price ?? 0;
      const hasEligibleAlternate = !!alternate && !altBlocked && altPrice > 0;
      // Show popup via global host using existing <pop-up> + DiscontinuedPopupComponent
      if (hasEligibleAlternate) {
        return await new Promise((resolve): void => {
          let resolved = false;
          // Safety timeout: if popup is closed without explicit action, treat as cancel
          // This should never trigger if callbacks work correctly, but provides a safety net
          const timeout = setTimeout((): void => {
            if (!resolved) {
              resolved = true;
              resolve({ allowed: false });
            }
          }, 10000); // 10 second timeout (safety net in case callbacks fail)

          const onConfirmHandler = (): void => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              resolve({ allowed: true, overrideProduct: alternateJson });
            }
          };

          const onCancelHandler = (): void => {
            if (!resolved) {
              resolved = true;
              clearTimeout(timeout);
              resolve({ allowed: false });
            }
          };

          this.broadcast.broadcast('OPEN_DISCONTINUED_POPUP', {
            oldProduct: product,
            newProduct: alternateJson,
            onConfirm: onConfirmHandler,
            onCancel: onCancelHandler
          });
        });
      }

      // No eligible alternate → show info popup and block
      this.broadcast.broadcast('OPEN_DISCONTINUED_INFO', { product });
      return { allowed: false };
    } catch (err) {
      // Fail safe: if anything goes wrong, block to avoid bad cart state
      return { allowed: false };
    }
  }

  private sendCartUpdateEvent(eventParams: CartUpdateEventParams): void {
    if (!eventParams.eventName) {
      return;
    }

    const removedEvents = ['HP_recently_viewed_cart_update', 'SP_recently_viewed_cart_update']
    const channels = removedEvents.includes(eventParams.eventName) ? [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ] : []

    this.eventLoggerService.cleverTapEvent(eventParams.eventName, JSON.stringify(eventParams), channels);
  }

  async loadCartProductsIntoStore(): Promise<Array<{ productId: string; quantity: number }>> {
    const aovOptions = { aovTask: false, maxPrice: 0 };
    const cartData = await this.getCartAndPricingDetails(0, aovOptions);
    const cartItems = cartData.cartItems ?? [];

    const cartProductQuantities: { [key: string]: number } = {};
    cartItems.forEach(item => {
      cartProductQuantities[item.productId] = item.quantity;
    });
    this.store.dispatch(fromActions.CartUpdateProductsBegin({ products: cartProductQuantities }));
    return cartItems;
  }

  async forceUpdateProductInCart(
    product: Catalog,
    action: string,
    options: {
      img?: HTMLImageElement;
      productsInCart: { [key: string]: number };
      cartItems?: Array<{ productId: string; quantity: number }>;
      section: string;
      flyToSelector?: string;
    },
  ): Promise<CartUpdateResult> {
    const cartOptions: CartUpdateOptions = {
      img: options.img,
      productsInCart: options.productsInCart,
      cartItems: options.cartItems,
      section: options.section,
      flyToSelector: options.flyToSelector,
      skipGuardCheck: true,
      skipVariantsCheck: true,
    };
    return this.addOrRemoveFromCart(product, action, cartOptions);
  }

  async getCartAndPricingDetails(
    cashBalance: number,
    aovDetails: AovDetails = { aovTask: false, maxPrice: 0 },
    resetCashBalance: boolean = true
  ): Promise<CartPricingResult> {
    if (resetCashBalance) {
      this.originalCashBalance = cashBalance;
    }

    this.cart = await this.conn.fetchNewCart(this.conn.user.id);
    this.cartItems = this.cart?.data?.lineItems || [];

    if (this.cartItems.length === 0) {
      return this.handleEmptyCart(aovDetails);
    }

    const pricingDetails = this.calculateDiscountForItems(aovDetails, this.originalCashBalance, this.cartItems);

    if (aovDetails.aovTask) {
      const freeProductResult = await this.handleFreeProduct(pricingDetails.totalSP, aovDetails, this.cartItems);

      if (freeProductResult.updatedCartNeeded) {
        const updatedCart = await this.conn.fetchNewCart(this.conn.user.id);
        const updatedCartItems = updatedCart?.data?.lineItems ?? [];
        const updatedPricing = this.calculateDiscountForItems(aovDetails, this.originalCashBalance, updatedCartItems);

        return {
          ...updatedPricing,
          freeProductResult,
          cartItems: updatedCartItems
        };
      }

      return { ...pricingDetails, freeProductResult, cartItems: this.cartItems };
    }

    return { ...pricingDetails, cartItems: this.cartItems };
  }

  animateAddToCart(img?: HTMLImageElement, flyToSelector?: string): void {
    if (!img) return;

    const image = new Image();
    image.src = img.src;
    Object.assign(image.style, {
      position: 'absolute',
      top: `${img.getBoundingClientRect().top}px`,
      right: `${this.window.nativeWindow.innerWidth - img.getBoundingClientRect().right}px`,
      height: `${img.offsetHeight}px`,
      width: 'auto',
      zIndex: '1000'
    });

    if (flyToSelector) {
      const target = this.window.nativeWindow.document.querySelector(flyToSelector) as HTMLElement | null;
      if (target) {
        const rect = target.getBoundingClientRect();
        image.style.setProperty('--fly-end-top', `${rect.top}px`);
        image.style.setProperty('--fly-end-right', `${this.window.nativeWindow.innerWidth - rect.right}px`);
      }
    }

    image.classList.add('fly-animation');
    image.onanimationend = () => image.remove();
    this.window.nativeWindow.document.body.appendChild(image);
  }

  private shouldSkipVariantsCheck(options: CartUpdateOptions): boolean {
    if (options.skipVariantsCheck) return true;

    if (!this.experimentService.addToCartVariantSelection) return true;

    const currentUrl = this.router.url;
    const isReorderPath = currentUrl.includes('/user/order/reorder');

    if (isReorderPath) {
      // On reorder path, check 'from' query param
      const fromParam = this.activatedRoute.snapshot.queryParams['from'];
      return this.skipVariantsCheckForFromParams.includes(fromParam);
    }

    // On other paths, check 'section' in options
    if (options.section) {
      return this.skipVariantsCheckForSections.includes(options.section);
    }

    return false;
  }


  shouldShowPregnancyBottomSheet(data: {
    marketingTags: Array<ValueOf<typeof ApiClientConstant.User.MarketingTags>>,
    gender: ValueOf<typeof ApiClientConstant.User.Gender>,
    safteyProfile: string,
    overridePregnancyCheck?: boolean,
    section: string
  }): { show: boolean; activeTag?: ActivePregnancyTag } {
    const restrictedShopExperiment = this.experimentService.getRestrictedShopExperiment();
    const { marketingTags, gender, safteyProfile, overridePregnancyCheck, section } = data;

    const forbiddenSections = [this.appConfig.Shared.Shop.Sections.BOGO, this.appConfig.Shared.Shop.Sections.PERSONALIZED_BOGO, this.appConfig.Shared.Shop.Sections.REORDER].includes(section);
    if (forbiddenSections) return { show: false };
    if (!safteyProfile) return { show: false };
    if (!restrictedShopExperiment?.variant?.pregnancy) return { show: false };
    if (overridePregnancyCheck) return { show: false };

    const activeTag = this.userService.getActivePregnancyTag(marketingTags);
    if (!activeTag) return { show: false };

    const isFemaleGender = gender === ApiClientConstant.User.Gender.FEMALE;
    if (!isFemaleGender) return { show: false };

    const tagSafetyProfiles = restrictedShopExperiment?.variant?.[activeTag] || [];
    const isRestrictedProduct = tagSafetyProfiles.includes(safteyProfile);

    return isRestrictedProduct ? { show: true, activeTag } : { show: false };
  }

  shouldShowApprovalBottomSheet(data: {
    requiresApproval: string | undefined,
    overrideApprovalCheck: boolean,
    section: string,
  }): boolean {
    const { requiresApproval, overrideApprovalCheck, section } = data;
    if (overrideApprovalCheck) return false;

    const forbiddenSections = [this.appConfig.Shared.Shop.Sections.BOGO, this.appConfig.Shared.Shop.Sections.PERSONALIZED_BOGO, this.appConfig.Shared.Shop.Sections.REORDER].includes(section);
    if (forbiddenSections) return false;

    const restrictedShopExperiment = this.experimentService.getRestrictedShopExperiment();
    if (!restrictedShopExperiment?.enable) return false;
    if (!restrictedShopExperiment?.variant?.approval) return false;

    return requiresApproval === ApiClientConstant.Catalog.RequiresApprovalType.ALWAYS;
  }

  shouldShowTeenagerBottomSheet(data: {
    age: number,
    safteyProfile: string,
    section: string
  }): boolean {
    const { safteyProfile, section } = data;
    let { age } = data;
    if (typeof age === 'string') {
      age = Number.parseInt(age, 10);
    }

    const forbiddenSections = [this.appConfig.Shared.Shop.Sections.BOGO, this.appConfig.Shared.Shop.Sections.PERSONALIZED_BOGO, this.appConfig.Shared.Shop.Sections.REORDER, this.appConfig.Shared.Shop.Sections.REORDER].includes(section);
    if (forbiddenSections) return false;

    const restrictedShopExperiment = this.experimentService.getRestrictedShopExperiment();
    if (!restrictedShopExperiment?.variant?.teenager) return false;
    if (!age || Number.isNaN(age)) return false;
    if (age > this.appConfig.Shared.User.Age.TEENAGER_AGE) return false;

    const isTeenagerRestrictedProduct = restrictedShopExperiment?.variant?.teenager_safety_profile?.includes(safteyProfile);

    return isTeenagerRestrictedProduct;
  }

  private async handleAddProduct(
    product: any,
    quantity: number,
    options: CartUpdateOptions
  ): Promise<CartUpdateResult> {
    if (options.img) {
      this.animateAddToCart(options.img, options.flyToSelector);
    }
    const newQuantity = quantity + 1;
    options.productsInCart[product.objectId] = newQuantity;
    const params = this.buildCartParams(product, options);
    await this.conn.addProductToNewCart(params);

    // Broadcast cart update event so parent components can refresh their cart state
    this.broadcast.broadcast('CART_UPDATED', { productId: product.objectId, action: 'add' });

    return this.createSuccessResult(newQuantity, options.productsInCart);
  }

  private async handleRemoveProduct(
    product: any,
    quantity: number,
    options: CartUpdateOptions
  ): Promise<CartUpdateResult> {
    try {
      const newQuantity = quantity - 1;
      options.productsInCart[product.objectId] = newQuantity;
      await this.conn.removeProductFromNewCart({
        productId: product.objectId,
        quantity: 1
      });

      // Broadcast cart update event so parent components can refresh their cart state
      this.broadcast.broadcast('CART_UPDATED', { productId: product.objectId, action: 'remove' });

      return this.createSuccessResult(newQuantity, options.productsInCart);
    } catch (error) {
      return this.createFailureResult(quantity, options.productsInCart);
    }
  }

  private buildCartParams(product: any, options: CartUpdateOptions): any {
    const arr1 = Object.keys(options.productsInCart);
    const commonElements = options.cartItems?.filter((element: any) =>
      arr1.includes(element.productId)) || [];

    const tempData = { ...options.productsInCart };
    commonElements.forEach((each: any) => {
      tempData[each.productId] = each.quantity;
    });

    const params: any = {
      productId: product.objectId,
      quantity: tempData[product.objectId] ? 1 : 0,
      section: options.section || 'unknown',
    };

    return params;
  }

  private calculateDiscountForItems(
    aovData: AovDetails,
    cashBalance: number,
    cartItems: any[]
  ): { totalMRP: number, totalSP: number, aovBannerPercentage: number, discountPercentage: number } {
    const totals = cartItems.reduce((acc, item) => ({
      originalSP: acc.originalSP + (item.quantity * item.price),
      totalMRP: acc.totalMRP + (item.quantity * item.mrp)
    }), { originalSP: 0, totalMRP: 0 });

    const { originalSP, totalMRP } = totals;
    const flooredTotalMRP = Math.floor(totalMRP);
    const flooredOriginalSP = Math.floor(originalSP);

    const cashDiscount = Math.min(cashBalance, flooredOriginalSP);
    const totalSP = flooredOriginalSP - cashDiscount;
    const discountPercentage = this.getDiscountPercentage(flooredTotalMRP, totalSP);
    const aovBannerPercentage = aovData.aovTask ? Math.round((totalSP / aovData.maxPrice) * 100) : 0;

    return { totalMRP: flooredTotalMRP, totalSP, aovBannerPercentage, discountPercentage };
  }

  private getDiscountPercentage(totalMRP: number, totalSP: number): number {
    return totalMRP > totalSP ? Math.floor(((totalMRP - totalSP) * 100) / totalMRP) : 0;
  }

  private handleEmptyCart(aovDetails: AovDetails): CartPricingResult {
    if (aovDetails.aovTask) {
      this.updateAovBannerStyles(1, aovDetails, 0, '#D66736');
      return {
        totalMRP: 0,
        totalSP: 0,
        aovBannerPercentage: 1,
        discountPercentage: 0,
        freeProductResult: {
          productAdded: false,
          productRemoved: false,
          shouldShowConfetti: false,
          updatedCartNeeded: false
        },
        cartItems: []
      };
    }

    return {
      totalMRP: 0,
      totalSP: 0,
      aovBannerPercentage: 0,
      discountPercentage: 0,
      cartItems: []
    };
  }

  private async handleFreeProduct(finalTotalSP: number, aovData: any, cartItems: any[]): Promise<{
    productAdded: boolean;
    productRemoved: boolean;
    shouldShowConfetti: boolean;
    updatedCartNeeded: boolean;
  }> {
    const percentageDone = Math.round((finalTotalSP / aovData.maxPrice) * 100);
    const hasMultipleQuantity = cartItems.some(item =>
      item.productId === aovData.productID && item.quantity > 1
    );
    const hasProduct = cartItems.some(item => item.productId === aovData.productID);

    const result = {
      productAdded: false,
      productRemoved: false,
      shouldShowConfetti: false,
      updatedCartNeeded: false
    };

    if (finalTotalSP < aovData.maxPrice || hasMultipleQuantity) {
      if (hasProduct) {
        await this.conn.removeProductFromNewCart({
          productId: aovData.productID,
          quantity: 1,
        });
        result.productRemoved = true;
        result.updatedCartNeeded = true;
      }
      this.updateAovBannerStyles(percentageDone, aovData, finalTotalSP, '#D66736');
    } else {
      this.updateAovBannerStyles(100, aovData, aovData.maxPrice, '#5EAC73');

      if (!hasProduct) {
        await this.conn.addProductToNewCart({
          productId: aovData.productID,
          quantity: 1,
          section: 'aov_free_product',
        });
        result.productAdded = true;
        result.shouldShowConfetti = true;
        result.updatedCartNeeded = true;
      }
    }

    return result;
  }

  private updateAovBannerStyles(percentage: number, aovData: any, finalSP: number, backgroundColor: string): void {
    this.barStyle = {
      amountRemaining: aovData.maxPrice - finalSP,
      width: `${percentage}%`,
      backgroundColor
    };
  }

  private createSuccessResult(quantity: number, productsInCart: { [key: string]: number }): CartUpdateResult {
    return {
      success: true,
      updatedQuantity: quantity,
      shouldUpdateStore: true,
      updatedProductsInCart: productsInCart
    };
  }

  private createFailureResult(quantity: number, productsInCart: { [key: string]: number }): CartUpdateResult {
    return {
      success: false,
      updatedQuantity: quantity,
      shouldUpdateStore: false,
      updatedProductsInCart: productsInCart
    };
  }
}
